import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Brain, 
  Heart, 
  Wind, 
  Volume2, 
  VolumeX,
  Play,
  Pause,
  RefreshCw,
  Clock,
  ChevronRight,
  Award,
  Calendar,
  TrendingUp,
  Headphones,
  Moon,
  Sun,
  Cloud,
  Waves,
  Trees,
  Mountain,
  Star,
  Info,
  Lock,
  Unlock,
  Bell,
  Music,
  Volume1,
  Settings
} from 'lucide-react';

/**
 * MindfulnessHub Component with Audio Support
 * 
 * Based on GitHub best practices and patterns from:
 * - Headspace, Calm, Insight Timer (meditation app patterns)
 * - Healthcare React applications (OpenMRS, Medplum)
 * - Web Audio API best practices
 * 
 * Features:
 * - Tablet-optimized responsive grid layout
 * - Guided meditation sessions with audio
 * - Breathing exercises with visual and audio cues
 * - Background soundscapes
 * - Progressive muscle relaxation
 * - Mindfulness techniques
 * - Progress tracking
 * - Full audio guidance support
 * 
 * Integration with App.js routing from Rhythm of life platform
 */

const MindfulnessHub = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('exercises');
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(70);
  const [selectedSoundscape, setSelectedSoundscape] = useState('silence');
  const [audioLoading, setAudioLoading] = useState(false);
  const [voiceGender, setVoiceGender] = useState('female');
  const [bellInterval, setBellInterval] = useState(0); // 0 = off, 1 = 1min, 5 = 5min
  const [userStats, setUserStats] = useState({
    totalSessions: 28,
    currentStreak: 7,
    totalMinutes: 420,
    favoriteTime: 'Morning'
  });
  const [completedSessions, setCompletedSessions] = useState([]);
  
  // Audio refs
  const intervalRef = useRef(null);
  const animationRef = useRef(null);
  const backgroundAudioRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const bellAudioRef = useRef(null);
  const breathingSoundRef = useRef(null);
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);

  // Meditation categories with exercises
  const meditationCategories = [
    {
      id: 'breathing',
      title: 'Breathing Exercises',
      icon: Wind,
      color: 'bg-blue-500',
      description: 'Calm your mind with controlled breathing',
      exercises: [
        {
          id: 'box-breathing',
          title: 'Box Breathing (4-4-4-4)',
          duration: 5,
          difficulty: 'Beginner',
          description: 'Navy SEAL technique for instant calm',
          instructions: 'Inhale 4s → Hold 4s → Exhale 4s → Hold 4s',
          pattern: [4, 4, 4, 4],
          benefits: ['Reduces stress', 'Improves focus', 'Lowers blood pressure']
        },
        {
          id: '478-breathing',
          title: '4-7-8 Breathing',
          duration: 4,
          difficulty: 'Beginner',
          description: 'Dr. Andrew Weil\'s relaxation breath',
          instructions: 'Inhale 4s → Hold 7s → Exhale 8s',
          pattern: [4, 7, 8, 0],
          benefits: ['Better sleep', 'Anxiety relief', 'Emotional regulation']
        },
        {
          id: 'belly-breathing',
          title: 'Diaphragmatic Breathing',
          duration: 10,
          difficulty: 'Beginner',
          description: 'Deep belly breathing for relaxation',
          instructions: 'Breathe deeply into your belly, expanding on inhale',
          pattern: [5, 2, 5, 2],
          benefits: ['Activates parasympathetic nervous system', 'Reduces cortisol']
        }
      ]
    },
    {
      id: 'meditation',
      title: 'Guided Meditations',
      icon: Brain,
      color: 'bg-purple-500',
      description: 'Mindfulness and awareness practices',
      exercises: [
        {
          id: 'body-scan',
          title: 'Body Scan Meditation',
          duration: 15,
          difficulty: 'Intermediate',
          description: 'Progressive relaxation from head to toe',
          instructions: 'Focus attention on each body part sequentially',
          benefits: ['Body awareness', 'Tension release', 'Pain management']
        },
        {
          id: 'loving-kindness',
          title: 'Loving-Kindness Meditation',
          duration: 12,
          difficulty: 'Beginner',
          description: 'Cultivate compassion for self and others',
          instructions: 'Send loving thoughts to yourself and others',
          benefits: ['Increased empathy', 'Positive emotions', 'Social connection']
        },
        {
          id: 'mindful-walking',
          title: 'Walking Meditation',
          duration: 20,
          difficulty: 'Beginner',
          description: 'Mindfulness in motion',
          instructions: 'Focus on each step and breath while walking',
          benefits: ['Physical activity', 'Present moment awareness', 'Grounding']
        }
      ]
    },
    {
      id: 'sleep',
      title: 'Sleep & Relaxation',
      icon: Moon,
      color: 'bg-indigo-500',
      description: 'Wind down and prepare for restful sleep',
      exercises: [
        {
          id: 'sleep-story',
          title: 'Peaceful Sleep Journey',
          duration: 25,
          difficulty: 'Beginner',
          description: 'Guided visualization for deep sleep',
          instructions: 'Listen and follow the calming narrative',
          benefits: ['Better sleep quality', 'Reduced insomnia', 'Dream enhancement']
        },
        {
          id: 'pmr',
          title: 'Progressive Muscle Relaxation',
          duration: 20,
          difficulty: 'Beginner',
          description: 'Systematic tension and release',
          instructions: 'Tense and relax muscle groups progressively',
          benefits: ['Physical relaxation', 'Anxiety reduction', 'Better sleep']
        },
        {
          id: 'yoga-nidra',
          title: 'Yoga Nidra',
          duration: 30,
          difficulty: 'Intermediate',
          description: 'Yogic sleep for deep restoration',
          instructions: 'Conscious relaxation while remaining aware',
          benefits: ['Deep rest', 'Stress relief', 'Mental clarity']
        }
      ]
    },
    {
      id: 'focus',
      title: 'Focus & Productivity',
      icon: TrendingUp,
      color: 'bg-green-500',
      description: 'Enhance concentration and mental clarity',
      exercises: [
        {
          id: 'pomodoro-breathing',
          title: 'Focus Breathing',
          duration: 5,
          difficulty: 'Beginner',
          description: 'Quick reset between work sessions',
          instructions: 'Energizing breath pattern for alertness',
          pattern: [4, 2, 4, 2],
          benefits: ['Improved focus', 'Energy boost', 'Mental clarity']
        },
        {
          id: 'visualization',
          title: 'Success Visualization',
          duration: 10,
          difficulty: 'Intermediate',
          description: 'Mental rehearsal for peak performance',
          instructions: 'Visualize achieving your goals vividly',
          benefits: ['Motivation', 'Confidence', 'Goal achievement']
        }
      ]
    },
    {
      id: 'anxiety',
      title: 'Anxiety & Stress Relief',
      icon: Heart,
      color: 'bg-rose-500',
      description: 'Calm anxious thoughts and feelings',
      exercises: [
        {
          id: 'grounding',
          title: '5-4-3-2-1 Grounding',
          duration: 8,
          difficulty: 'Beginner',
          description: 'Sensory grounding for anxiety',
          instructions: '5 things you see, 4 you hear, 3 you touch, 2 you smell, 1 you taste',
          benefits: ['Anxiety relief', 'Present moment awareness', 'Panic management']
        },
        {
          id: 'safe-place',
          title: 'Safe Place Visualization',
          duration: 15,
          difficulty: 'Beginner',
          description: 'Create a mental sanctuary',
          instructions: 'Imagine a place where you feel completely safe and calm',
          benefits: ['Emotional regulation', 'Stress relief', 'Inner peace']
        }
      ]
    }
  ];

  // Audio URLs - Using CDN links for demo (in production, host your own files)
  const audioSources = {
    soundscapes: {
      rain: 'https://cdn.freesound.org/previews/531/531947_6142149-lq.mp3',
      ocean: 'https://cdn.freesound.org/previews/456/456968_5123456-lq.mp3', 
      forest: 'https://cdn.freesound.org/previews/567/567890_7890123-lq.mp3',
      mountain: 'https://cdn.freesound.org/previews/234/234567_3456789-lq.mp3',
      // Alternative: Use base64 encoded short loops or Web Audio API generated sounds
    },
    bells: {
      tibetan: 'https://cdn.freesound.org/previews/189/189654_3456789-lq.mp3',
      crystal: 'https://cdn.freesound.org/previews/234/234567_4567890-lq.mp3',
      singing: 'https://cdn.freesound.org/previews/345/345678_5678901-lq.mp3'
    },
    guidance: {
      // These would be your hosted guided meditation audio files
      breathingIntro: '/audio/breathing-intro.mp3',
      bodyScan: '/audio/body-scan-guided.mp3',
      lovingKindness: '/audio/loving-kindness.mp3'
    }
  };

  // Sound/Nature backgrounds with audio properties
  const soundscapes = [
    { id: 'silence', name: 'Silence', icon: VolumeX, url: null },
    { id: 'rain', name: 'Rain', icon: Cloud, url: audioSources.soundscapes.rain },
    { id: 'ocean', name: 'Ocean Waves', icon: Waves, url: audioSources.soundscapes.ocean },
    { id: 'forest', name: 'Forest', icon: Trees, url: audioSources.soundscapes.forest },
    { id: 'mountain', name: 'Mountain Stream', icon: Mountain, url: audioSources.soundscapes.mountain }
  ];

  // Quick access exercises for tablets
  const quickExercises = [
    { 
      id: 'quick-calm',
      name: '2-Min Calm',
      icon: '😌',
      duration: 2
    },
    { 
      id: 'energy-boost',
      name: 'Energy Boost',
      icon: '⚡',
      duration: 3
    },
    { 
      id: 'lunch-break',
      name: 'Lunch Reset',
      icon: '🍃',
      duration: 5
    },
    { 
      id: 'bedtime',
      name: 'Bedtime Wind-down',
      icon: '🌙',
      duration: 10
    }
  ];

  // Initialize Web Audio API
  useEffect(() => {
    // Initialize audio context on user interaction
    const initAudio = () => {
      if (typeof window !== 'undefined' && !audioContextRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          audioContextRef.current = new AudioContext();
          
          // Create gain node for volume control
          gainNodeRef.current = audioContextRef.current.createGain();
          gainNodeRef.current.connect(audioContextRef.current.destination);
          gainNodeRef.current.gain.value = volume / 100;
        }
      }
    };

    // Initialize on first user interaction
    initAudio();

    // Create audio elements
    if (!backgroundAudioRef.current) {
      backgroundAudioRef.current = new Audio();
      backgroundAudioRef.current.loop = true;
      backgroundAudioRef.current.volume = 0.3; // Lower volume for background
    }

    if (!voiceAudioRef.current) {
      voiceAudioRef.current = new Audio();
      voiceAudioRef.current.volume = 0.8;
    }

    if (!bellAudioRef.current) {
      bellAudioRef.current = new Audio();
      bellAudioRef.current.volume = 0.5;
    }

    if (!breathingSoundRef.current) {
      breathingSoundRef.current = new Audio();
      breathingSoundRef.current.volume = 0.4;
    }

    return () => {
      // Cleanup audio on unmount
      stopAllAudio();
      stopBackgroundSound(); // Ensure background is stopped
      
      // Clear all intervals
      Object.values(backgroundIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
      
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Store background sound intervals and sources
  const backgroundSourcesRef = useRef({});
  const backgroundIntervalsRef = useRef({});

  // Audio Control Functions
  const playBackgroundSound = (soundscapeId) => {
    // Stop any existing background sound first
    stopBackgroundSound();
    
    if (soundscapeId === 'silence' || !audioContextRef.current) return;
    
    // Resume audio context if suspended (browser requirement)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    
    synthesizeBackgroundSound(soundscapeId);
  };

  const synthesizeBackgroundSound = (type) => {
    if (!audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const now = ctx.currentTime;

    switch(type) {
      case 'rain':
        // Create continuous rain sound with white noise
        const bufferSize = 2 * ctx.sampleRate;
        const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        
        for (let i = 0; i < bufferSize; i++) {
          output[i] = (Math.random() * 2 - 1) * 0.5; // Reduced amplitude
        }
        
        const whiteNoise = ctx.createBufferSource();
        whiteNoise.buffer = noiseBuffer;
        whiteNoise.loop = true;
        
        const rainFilter = ctx.createBiquadFilter();
        rainFilter.type = 'lowpass';
        rainFilter.frequency.value = 1000;
        rainFilter.Q.value = 1;
        
        const rainGain = ctx.createGain();
        rainGain.gain.value = 0.15; // Soft background volume
        
        whiteNoise.connect(rainFilter);
        rainFilter.connect(rainGain);
        rainGain.connect(gainNodeRef.current);
        
        whiteNoise.start(now);
        backgroundSourcesRef.current.rain = whiteNoise;
        break;
        
      case 'ocean':
        // Create ocean waves with modulated noise
        const oceanBufferSize = 4 * ctx.sampleRate;
        const oceanBuffer = ctx.createBuffer(1, oceanBufferSize, ctx.sampleRate);
        const oceanData = oceanBuffer.getChannelData(0);
        
        // Generate ocean-like noise
        for (let i = 0; i < oceanBufferSize; i++) {
          const envelope = Math.sin((i / oceanBufferSize) * Math.PI * 2) * 0.3 + 0.7;
          oceanData[i] = (Math.random() * 2 - 1) * envelope * 0.3;
        }
        
        const oceanSource = ctx.createBufferSource();
        oceanSource.buffer = oceanBuffer;
        oceanSource.loop = true;
        
        const oceanFilter = ctx.createBiquadFilter();
        oceanFilter.type = 'lowpass';
        oceanFilter.frequency.value = 800;
        
        const oceanGain = ctx.createGain();
        oceanGain.gain.value = 0.2;
        
        // Add LFO for wave movement
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        lfo.type = 'sine';
        lfo.frequency.value = 0.08; // Very slow oscillation
        lfoGain.gain.value = 200;
        
        lfo.connect(lfoGain);
        lfoGain.connect(oceanFilter.frequency);
        
        oceanSource.connect(oceanFilter);
        oceanFilter.connect(oceanGain);
        oceanGain.connect(gainNodeRef.current);
        
        oceanSource.start(now);
        lfo.start(now);
        
        backgroundSourcesRef.current.ocean = { source: oceanSource, lfo: lfo };
        break;
        
      case 'forest':
        // Create ambient forest sound
        const forestBufferSize = 2 * ctx.sampleRate;
        const forestBuffer = ctx.createBuffer(1, forestBufferSize, ctx.sampleRate);
        const forestData = forestBuffer.getChannelData(0);
        
        // Generate soft rustling noise
        for (let i = 0; i < forestBufferSize; i++) {
          forestData[i] = (Math.random() * 2 - 1) * 0.05;
        }
        
        const forestSource = ctx.createBufferSource();
        forestSource.buffer = forestBuffer;
        forestSource.loop = true;
        
        const forestFilter = ctx.createBiquadFilter();
        forestFilter.type = 'bandpass';
        forestFilter.frequency.value = 1000;
        forestFilter.Q.value = 0.5;
        
        const forestGain = ctx.createGain();
        forestGain.gain.value = 0.1;
        
        forestSource.connect(forestFilter);
        forestFilter.connect(forestGain);
        forestGain.connect(gainNodeRef.current);
        
        forestSource.start(now);
        backgroundSourcesRef.current.forest = forestSource;
        
        // Add periodic bird chirps
        const playChirp = () => {
          if (!audioContextRef.current || selectedSoundscape !== 'forest') return;
          
          const chirpCtx = audioContextRef.current;
          const chirpTime = chirpCtx.currentTime;
          
          const chirp = chirpCtx.createOscillator();
          const chirpGain = chirpCtx.createGain();
          
          chirp.type = 'sine';
          chirp.frequency.setValueAtTime(2500 + Math.random() * 1000, chirpTime);
          chirp.frequency.exponentialRampToValueAtTime(3000 + Math.random() * 500, chirpTime + 0.05);
          chirp.frequency.exponentialRampToValueAtTime(2000 + Math.random() * 500, chirpTime + 0.1);
          
          chirpGain.gain.setValueAtTime(0, chirpTime);
          chirpGain.gain.linearRampToValueAtTime(0.05, chirpTime + 0.01);
          chirpGain.gain.linearRampToValueAtTime(0, chirpTime + 0.1);
          
          chirp.connect(chirpGain);
          chirpGain.connect(gainNodeRef.current);
          
          chirp.start(chirpTime);
          chirp.stop(chirpTime + 0.15);
        };
        
        // Play chirp immediately and set interval
        playChirp();
        backgroundIntervalsRef.current.forest = setInterval(playChirp, 3000 + Math.random() * 2000);
        break;
        
      case 'mountain':
        // Create mountain stream sound
        const streamBufferSize = 3 * ctx.sampleRate;
        const streamBuffer = ctx.createBuffer(1, streamBufferSize, ctx.sampleRate);
        const streamData = streamBuffer.getChannelData(0);
        
        // Generate bubbling water sound
        for (let i = 0; i < streamBufferSize; i++) {
          streamData[i] = (Math.random() * 2 - 1) * 0.3;
        }
        
        const streamSource = ctx.createBufferSource();
        streamSource.buffer = streamBuffer;
        streamSource.loop = true;
        
        const streamFilter1 = ctx.createBiquadFilter();
        streamFilter1.type = 'highpass';
        streamFilter1.frequency.value = 400;
        
        const streamFilter2 = ctx.createBiquadFilter();
        streamFilter2.type = 'lowpass';
        streamFilter2.frequency.value = 3000;
        
        const streamGain = ctx.createGain();
        streamGain.gain.value = 0.12;
        
        streamSource.connect(streamFilter1);
        streamFilter1.connect(streamFilter2);
        streamFilter2.connect(streamGain);
        streamGain.connect(gainNodeRef.current);
        
        streamSource.start(now);
        backgroundSourcesRef.current.mountain = streamSource;
        break;
    }
  };

  const stopBackgroundSound = () => {
    // Stop all background sources
    Object.keys(backgroundSourcesRef.current).forEach(key => {
      const source = backgroundSourcesRef.current[key];
      if (source) {
        if (source.stop) {
          try {
            source.stop();
          } catch (e) {
            // Source might already be stopped
          }
        }
        // Handle ocean which has multiple sources
        if (source.source) {
          try {
            source.source.stop();
          } catch (e) {}
        }
        if (source.lfo) {
          try {
            source.lfo.stop();
          } catch (e) {}
        }
      }
    });
    backgroundSourcesRef.current = {};
    
    // Clear all intervals
    Object.keys(backgroundIntervalsRef.current).forEach(key => {
      clearInterval(backgroundIntervalsRef.current[key]);
    });
    backgroundIntervalsRef.current = {};
  };

  const playBellSound = () => {
    if (!audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const now = ctx.currentTime;
    
    // Create a bell sound using Web Audio API
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    osc.frequency.value = 528; // Solfeggio frequency for transformation
    osc.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.5, now);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 2);
    
    osc.connect(gainNode);
    gainNode.connect(gainNodeRef.current);
    
    osc.start(now);
    osc.stop(now + 2);

    // Add harmonics for richer bell sound
    for (let i = 2; i <= 4; i++) {
      const harmonic = ctx.createOscillator();
      const harmonicGain = ctx.createGain();
      
      harmonic.frequency.value = 528 * i;
      harmonic.type = 'sine';
      
      harmonicGain.gain.setValueAtTime(0.5 / i, now);
      harmonicGain.gain.exponentialRampToValueAtTime(0.01, now + 2 / i);
      
      harmonic.connect(harmonicGain);
      harmonicGain.connect(gainNodeRef.current);
      
      harmonic.start(now);
      harmonic.stop(now + 2);
    }
  };

  const speakText = (text, rate = 1) => {
    // Use Web Speech API for voice guidance
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel(); // Cancel any ongoing speech
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = rate;
      utterance.pitch = 1;
      utterance.volume = isMuted ? 0 : volume / 100;
      
      // Select voice based on preference
      const voices = window.speechSynthesis.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.toLowerCase().includes(voiceGender)
      );
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const playBreathingSound = (phase) => {
    if (!audioContextRef.current || isMuted) return;

    const ctx = audioContextRef.current;
    const now = ctx.currentTime;
    
    // Create breathing sound effect
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.type = 'sine';
    filter.type = 'lowpass';
    
    if (phase === 'inhale') {
      // Rising pitch for inhale
      osc.frequency.setValueAtTime(100, now);
      osc.frequency.exponentialRampToValueAtTime(200, now + 4);
      filter.frequency.setValueAtTime(500, now);
      filter.frequency.linearRampToValueAtTime(2000, now + 4);
      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.1, now + 4);
      
      // Voice guidance
      if (currentTime === 0 || currentTime % 60 === 0) {
        speakText('Breathe in slowly', 0.8);
      }
    } else if (phase === 'exhale') {
      // Falling pitch for exhale
      osc.frequency.setValueAtTime(200, now);
      osc.frequency.exponentialRampToValueAtTime(100, now + 4);
      filter.frequency.setValueAtTime(2000, now);
      filter.frequency.linearRampToValueAtTime(500, now + 4);
      gainNode.gain.setValueAtTime(0.1, now);
      gainNode.gain.linearRampToValueAtTime(0, now + 4);
      
      // Voice guidance
      if (currentTime % 60 === 30) {
        speakText('And breathe out', 0.8);
      }
    } else if (phase === 'hold') {
      // Voice guidance for hold
      speakText('Hold', 0.8);
      return; // No sound for hold phase
    }
    
    osc.connect(filter);
    filter.connect(gainNode);
    gainNode.connect(gainNodeRef.current);
    
    osc.start(now);
    osc.stop(now + 4);
  };

  const stopAllAudio = () => {
    stopBackgroundSound();
    window.speechSynthesis?.cancel();
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.currentTime = 0;
    }
    if (bellAudioRef.current) {
      bellAudioRef.current.pause();
      bellAudioRef.current.currentTime = 0;
    }
  };

  // Handle volume change
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume / 100;
    }
    if (backgroundAudioRef.current) {
      backgroundAudioRef.current.volume = (newVolume / 100) * 0.3;
    }
    if (voiceAudioRef.current) {
      voiceAudioRef.current.volume = (newVolume / 100) * 0.8;
    }
  };

  // Timer and animation handlers
  useEffect(() => {
    if (isPlaying && selectedExercise) {
      // Start background sound if selected (with slight delay for audio context)
      if (selectedSoundscape !== 'silence') {
        const soundTimer = setTimeout(() => {
          playBackgroundSound(selectedSoundscape);
        }, 200);
      }

      // Initial voice guidance
      if (currentTime === 0) {
        const introText = `Starting ${selectedExercise.title}. ${selectedExercise.instructions || 'Let\'s begin.'}`;
        speakText(introText, 0.9);
      }

      // Set up interval timer
      intervalRef.current = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + 1;
          
          // Bell interval sounds
          if (bellInterval > 0 && newTime > 0 && newTime % (bellInterval * 60) === 0) {
            playBellSound();
          }
          
          // Check if session complete
          if (newTime >= duration * 60) {
            handleComplete();
            return 0;
          }
          
          // Periodic voice guidance
          if (newTime % 60 === 0 && newTime > 0) {
            const minutesRemaining = duration - Math.floor(newTime / 60);
            if (minutesRemaining === 1) {
              speakText('One minute remaining', 0.9);
            } else if (minutesRemaining === 5) {
              speakText('Five minutes remaining', 0.9);
            }
          }
          
          return newTime;
        });
      }, 1000);

      // Breathing animation with sound
      if (selectedExercise.pattern) {
        animateBreathing(selectedExercise.pattern);
      }
    } else {
      // Stop all audio when paused
      if (!isPlaying) {
        window.speechSynthesis?.cancel();
        stopBackgroundSound();
      }
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, selectedExercise, duration, bellInterval]);

  // Handle soundscape changes separately
  useEffect(() => {
    if (isPlaying) {
      stopBackgroundSound();
      if (selectedSoundscape !== 'silence') {
        setTimeout(() => {
          playBackgroundSound(selectedSoundscape);
        }, 100);
      }
    }
  }, [selectedSoundscape]);

  // Breathing animation logic with sound
  const animateBreathing = (pattern) => {
    const [inhale, hold1, exhale, hold2] = pattern;
    let phase = 0;
    let phaseTime = 0;
    const phases = ['inhale', 'hold', 'exhale', 'hold'];
    const phaseDurations = [inhale, hold1, exhale, hold2];
    let lastPhase = '';

    const animate = () => {
      if (!isPlaying) return;
      
      phaseTime += 0.1;
      
      if (phaseTime >= phaseDurations[phase]) {
        phase = (phase + 1) % 4;
        phaseTime = 0;
        const newPhase = phases[phase];
        setBreathPhase(newPhase);
        
        // Play sound for new phase
        if (newPhase !== lastPhase) {
          playBreathingSound(newPhase);
          lastPhase = newPhase;
        }
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  };

  const handleExerciseSelect = (exercise) => {
    setSelectedExercise(exercise);
    setDuration(exercise.duration);
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const handlePlayPause = () => {
    // Resume audio context if it's suspended (browser requirement)
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().then(() => {
        console.log('Audio context resumed');
      });
    }
    
    if (!isPlaying) {
      // Starting playback
      setIsPlaying(true);
      
      // Start background sound if selected
      if (selectedSoundscape !== 'silence') {
        setTimeout(() => {
          playBackgroundSound(selectedSoundscape);
        }, 100); // Small delay to ensure audio context is ready
      }
    } else {
      // Pausing playback
      setIsPlaying(false);
      stopBackgroundSound();
    }
  };

  const handleReset = () => {
    setCurrentTime(0);
    setIsPlaying(false);
    setBreathPhase('inhale');
  };

  const handleComplete = () => {
    setIsPlaying(false);
    
    // Play completion sound and message
    playBellSound();
    speakText('Well done! Session complete. Take a moment to notice how you feel.', 0.9);
    
    // Stop background sounds after message
    setTimeout(() => {
      stopAllAudio();
    }, 5000);
    
    const session = {
      exercise: selectedExercise.title,
      duration: duration,
      completedAt: new Date().toISOString()
    };
    setCompletedSessions([...completedSessions, session]);
    setUserStats(prev => ({
      ...prev,
      totalSessions: prev.totalSessions + 1,
      totalMinutes: prev.totalMinutes + duration
    }));
    // Show completion modal or notification
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header with Stats - Tablet Optimized */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                Mindfulness Hub
              </h1>
              <p className="text-gray-600">
                Your daily destination for peace, focus, and mental clarity
              </p>
              {/* Audio Test Button */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => {
                    // Initialize audio context if needed
                    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                      audioContextRef.current.resume();
                    }
                    if (!audioContextRef.current) {
                      const AudioContext = window.AudioContext || window.webkitAudioContext;
                      if (AudioContext) {
                        audioContextRef.current = new AudioContext();
                        gainNodeRef.current = audioContextRef.current.createGain();
                        gainNodeRef.current.connect(audioContextRef.current.destination);
                        gainNodeRef.current.gain.value = volume / 100;
                      }
                    }
                    playBellSound();
                    speakText('Audio is working. Welcome to your mindfulness practice.', 0.9);
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-2"
                >
                  <Headphones className="w-4 h-4" />
                  Test Audio
                </button>
                <button
                  onClick={() => {
                    // Test background sounds
                    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                      audioContextRef.current.resume();
                    }
                    stopBackgroundSound();
                    playBackgroundSound('rain');
                    setTimeout(() => {
                      playBackgroundSound('ocean');
                      setTimeout(() => {
                        playBackgroundSound('forest');
                        setTimeout(() => {
                          stopBackgroundSound();
                        }, 2000);
                      }, 2000);
                    }, 2000);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-2"
                >
                  <Music className="w-4 h-4" />
                  Test Backgrounds
                </button>
              </div>
            </div>
            
            {/* User Stats - Grid for tablets */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full lg:w-auto">
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-purple-700">
                  {userStats.currentStreak}
                </div>
                <div className="text-xs text-purple-600">Day Streak</div>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-blue-700">
                  {userStats.totalSessions}
                </div>
                <div className="text-xs text-blue-600">Sessions</div>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-green-700">
                  {userStats.totalMinutes}
                </div>
                <div className="text-xs text-green-600">Minutes</div>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-orange-700">
                  <Star className="w-6 h-6 mx-auto" />
                </div>
                <div className="text-xs text-orange-600">Level 2</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Access Bar - Horizontal scroll for tablets */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-8 overflow-x-auto">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Quick Sessions</h3>
          <div className="flex gap-3 min-w-max">
            {quickExercises.map((exercise) => (
              <button
                key={exercise.id}
                onClick={() => handleExerciseSelect(exercise)}
                className="flex flex-col items-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl hover:shadow-md transition-all duration-300 min-w-[100px]"
              >
                <span className="text-3xl mb-2">{exercise.icon}</span>
                <span className="text-xs font-medium text-gray-700">{exercise.name}</span>
                <span className="text-xs text-gray-500">{exercise.duration} min</span>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content Area - Two Column Layout for Tablets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Exercise Categories - 2 columns on tablet */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Mindfulness Exercises
              </h2>
              
              {/* Category Grid - Responsive for tablets */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {meditationCategories.map((category) => (
                  <div
                    key={category.id}
                    className="border border-gray-200 rounded-xl hover:shadow-lg transition-all duration-300"
                  >
                    <div
                      className={`${category.color} text-white p-4 rounded-t-xl`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <category.icon className="w-6 h-6" />
                          <h3 className="font-semibold text-lg">{category.title}</h3>
                        </div>
                        <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
                          {category.exercises.length} exercises
                        </span>
                      </div>
                      <p className="text-sm text-white/90 mt-2">
                        {category.description}
                      </p>
                    </div>
                    
                    <div className="p-4 space-y-3">
                      {category.exercises.map((exercise) => (
                        <button
                          key={exercise.id}
                          onClick={() => handleExerciseSelect(exercise)}
                          className="w-full text-left p-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 group-hover:text-purple-700">
                                {exercise.title}
                              </h4>
                              <p className="text-sm text-gray-600 mt-1">
                                {exercise.description}
                              </p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {exercise.duration} min
                                </span>
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                  {exercise.difficulty}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Player/Timer Panel - Sticky on tablets */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
              {selectedExercise ? (
                <>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedExercise.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {selectedExercise.description}
                  </p>

                  {/* Breathing Visual with Audio Indicator */}
                  {selectedExercise.pattern && (
                    <div className="flex justify-center mb-6">
                      <div className="relative">
                        <div className={`
                          w-32 h-32 md:w-40 md:h-40 rounded-full flex items-center justify-center
                          transition-all duration-1000 relative
                          ${breathPhase === 'inhale' ? 'bg-blue-200 scale-125' :
                            breathPhase === 'exhale' ? 'bg-blue-100 scale-75' :
                            'bg-purple-200 scale-100'}
                        `}>
                          <span className="text-lg font-medium text-gray-700 capitalize z-10">
                            {breathPhase}
                          </span>
                          {/* Audio Wave Animation */}
                          {isPlaying && !isMuted && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="absolute w-full h-full rounded-full border-2 border-purple-400 opacity-20 animate-ping"></div>
                              <div className="absolute w-full h-full rounded-full border-2 border-purple-400 opacity-20 animate-ping animation-delay-200"></div>
                              <div className="absolute w-full h-full rounded-full border-2 border-purple-400 opacity-20 animate-ping animation-delay-400"></div>
                            </div>
                          )}
                        </div>
                        {/* Sound Indicator */}
                        {isPlaying && !isMuted && (
                          <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1 animate-pulse">
                            <Volume2 className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timer Display */}
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-gray-900">
                      {formatTime(currentTime)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      of {duration}:00 minutes
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(currentTime / (duration * 60)) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Instructions */}
                  {selectedExercise.instructions && (
                    <div className="bg-purple-50 rounded-lg p-4 mb-4">
                      <h4 className="text-sm font-semibold text-purple-800 mb-2 flex items-center">
                        <Info className="w-4 h-4 mr-2" />
                        Instructions
                      </h4>
                      <p className="text-sm text-purple-700">
                        {selectedExercise.instructions}
                      </p>
                    </div>
                  )}

                  {/* Benefits */}
                  {selectedExercise.benefits && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Benefits:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedExercise.benefits.map((benefit, index) => (
                          <span key={index} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Control Buttons */}
                  <div className="flex gap-3 justify-center mb-4">
                    <button
                      onClick={handleReset}
                      className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      <RefreshCw className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={handlePlayPause}
                      className="p-4 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg transition-all transform hover:scale-105"
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6" />
                      ) : (
                        <Play className="w-6 h-6 ml-0.5" />
                      )}
                    </button>
                    <button
                      onClick={() => setIsMuted(!isMuted)}
                      className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5 text-gray-700" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-gray-700" />
                      )}
                    </button>
                  </div>

                  {/* Volume Control */}
                  <div className="mb-4">
                    <div className="flex items-center gap-3">
                      <Volume1 className="w-4 h-4 text-gray-500" />
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={isMuted ? 0 : volume}
                        onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                        className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                      />
                      <Volume2 className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600 w-10">{isMuted ? 0 : volume}%</span>
                    </div>
                  </div>

                  {/* Audio Settings */}
                  <div className="border-t pt-4 mb-4">
                    <button
                      className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 mb-3"
                      onClick={() => document.getElementById('audioSettings').classList.toggle('hidden')}
                    >
                      <span className="flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Audio Settings
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    
                    <div id="audioSettings" className="space-y-3 hidden">
                      {/* Voice Gender Selection */}
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">Voice Guidance</label>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setVoiceGender('female')}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs ${
                              voiceGender === 'female' 
                                ? 'bg-purple-100 text-purple-700 border-purple-300' 
                                : 'bg-gray-50 text-gray-600 border-gray-200'
                            } border transition-colors`}
                          >
                            Female
                          </button>
                          <button
                            onClick={() => setVoiceGender('male')}
                            className={`flex-1 px-3 py-2 rounded-lg text-xs ${
                              voiceGender === 'male' 
                                ? 'bg-purple-100 text-purple-700 border-purple-300' 
                                : 'bg-gray-50 text-gray-600 border-gray-200'
                            } border transition-colors`}
                          >
                            Male
                          </button>
                        </div>
                      </div>
                      
                      {/* Bell Interval */}
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">
                          <Bell className="w-3 h-3 inline mr-1" />
                          Bell Interval
                        </label>
                        <div className="flex gap-2">
                          {[
                            { value: 0, label: 'Off' },
                            { value: 1, label: '1 min' },
                            { value: 5, label: '5 min' }
                          ].map(option => (
                            <button
                              key={option.value}
                              onClick={() => setBellInterval(option.value)}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs ${
                                bellInterval === option.value 
                                  ? 'bg-purple-100 text-purple-700 border-purple-300' 
                                  : 'bg-gray-50 text-gray-600 border-gray-200'
                              } border transition-colors`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Soundscape Selector */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Background Sound</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {soundscapes.map((sound) => (
                        <button
                          key={sound.id}
                          onClick={() => {
                            setSelectedSoundscape(sound.id);
                            if (isPlaying) {
                              playBackgroundSound(sound.id);
                            }
                          }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-sm ${
                            selectedSoundscape === sound.id
                              ? 'border-purple-300 bg-purple-50 text-purple-700'
                              : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                          }`}
                        >
                          <sound.icon className="w-4 h-4" />
                          <span className="text-xs">{sound.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-12">
                  <Brain className="w-16 h-16 mx-auto text-purple-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Select an Exercise
                  </h3>
                  <p className="text-sm text-gray-500">
                    Choose a mindfulness exercise from the categories to begin your practice
                  </p>
                </div>
              )}
            </div>

            {/* Recent Sessions - Below player on tablets */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                Recent Sessions
              </h3>
              <div className="space-y-3">
                {completedSessions.length > 0 ? (
                  completedSessions.slice(-3).reverse().map((session, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-700">{session.exercise}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(session.completedAt).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                        {session.duration} min
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No sessions completed yet. Start your first mindfulness practice!
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tips Section - Full width for tablets */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl shadow-lg p-8 mt-8 text-white">
          <h2 className="text-2xl font-bold mb-4">Mindfulness Tips</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <Sun className="w-6 h-6 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Morning Practice</h4>
                <p className="text-sm text-white/90">
                  Start your day with 5 minutes of mindful breathing to set a positive tone.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-6 h-6 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Consistency is Key</h4>
                <p className="text-sm text-white/90">
                  Even 2 minutes daily is better than occasional long sessions.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Heart className="w-6 h-6 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-1">Be Patient</h4>
                <p className="text-sm text-white/90">
                  Mindfulness is a skill that develops over time. Be kind to yourself.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Resources Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mt-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/self-help/cbt-worksheets')}
              className="p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all group"
            >
              <div className="text-purple-600 mb-2">📋</div>
              <h4 className="font-medium text-gray-900 group-hover:text-purple-700">CBT Worksheets</h4>
              <p className="text-xs text-gray-600 mt-1">Cognitive exercises</p>
            </button>
            <button 
              onClick={() => navigate('/self-help/stress-management')}
              className="p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all group"
            >
              <div className="text-purple-600 mb-2">🛡️</div>
              <h4 className="font-medium text-gray-900 group-hover:text-purple-700">Stress Management</h4>
              <p className="text-xs text-gray-600 mt-1">Coping strategies</p>
            </button>
            <button 
              onClick={() => navigate('/self-help/mood-tracker')}
              className="p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all group"
            >
              <div className="text-purple-600 mb-2">📊</div>
              <h4 className="font-medium text-gray-900 group-hover:text-purple-700">Mood Tracker</h4>
              <p className="text-xs text-gray-600 mt-1">Track your progress</p>
            </button>
            <button 
              onClick={() => navigate('/self-help/resources')}
              className="p-4 border border-gray-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-all group"
            >
              <div className="text-purple-600 mb-2">📚</div>
              <h4 className="font-medium text-gray-900 group-hover:text-purple-700">Resource Library</h4>
              <p className="text-xs text-gray-600 mt-1">Educational materials</p>
            </button>
          </div>
        </div>

        {/* Audio Permission Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Audio Features</p>
            <p>This mindfulness hub includes voice guidance, breathing sounds, and relaxing soundscapes. 
               For the best experience, ensure your device audio is enabled and use headphones for immersive practice.</p>
          </div>
        </div>
      </div>

      {/* Inline styles for animation delays */}
      <style jsx>{`
        @keyframes ping {
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        .animate-ping {
          animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default MindfulnessHub;